import React, { useState } from 'react'

import { Label } from 'semantic-ui-react'
import Papa from 'papaparse'

import { ImportCustomerModal } from 'components/ImportCustomerModal'
import CustomerImportConfirmationModal from 'organisms/CustomerImportConfirmationModal'
import CustomerImportTagsModal from 'components/CustomerImportTagsModal'
import { Loader } from 'shared/components/Loader'
import constants from 'config/constants'
import useSaveUserActivityLog from 'hooks/useSaveUserActivityLog'
import useFeatureToggle from 'hooks/useFeatureToggle'
import { languageOption } from 'organisms/FeedbackFilterGroup/constants'
import useAxios from 'hooks/useAxios'
import { isIsoDateTime } from 'libs/date'

const VALID_LANGUAGE_CODES = new Set(
  Object.values(languageOption).map((code) => code.toLowerCase())
)

export default function MultiStepCustomerImportModal({ onCsvUpload, closeModal }) {
  const [step, setStep] = useState(1)
  const [csvFile, setCsvFile] = useState(null)
  const [fileLoading, setFileLoading] = useState(false)
  /** @type {any[]} */
  const [csvContent, setCsvContent] = useState()
  const [counter, setCounter] = useState(0)
  const [commaSeparatedTags, setCommaSeparatedTags] = useState()
  const saveUserActivityLog = useSaveUserActivityLog()
  const { enableTypeDatetimeAttribute } = useFeatureToggle()

  const { data: customerAttributes, loading: attributesLoading } = useAxios({
    url: 'customers/attributes/?limit=1000',
    skip: !enableTypeDatetimeAttribute,
  })
  const customAttributes = enableTypeDatetimeAttribute ? customerAttributes?.results ?? [] : []

  function handleUploadClick(file) {
    setFileLoading(true)
    setCsvFile(file)
    Papa.parse(file, {
      skipEmptyLines: true,
      complete(results) {
        setCsvContent(results.data)
        setFileLoading(false)
        setStep(2)
      },
    })
  }

  if (step === 1) {
    return (
      <ImportCustomerModal
        file={csvFile}
        setFile={setCsvFile}
        fileLoading={fileLoading}
        onUpload={handleUploadClick}
      />
    )
  }

  if (step === 2 && csvContent && attributesLoading) {
    return <Loader withContainer />
  }

  if (step === 2 && csvContent) {
    const fieldMappingDataForValidation = createFieldMappingData(
      csvContent,
      1,
      true,
      customAttributes
    )
    const columnTypeMap = deriveColumnTypeMap(fieldMappingDataForValidation)
    const importDataValidationResult = validateImportData(csvContent, true, columnTypeMap)
    const dataLength = csvContent.length - 1
    const dataIndex = (counter % dataLength) + 1
    const fieldMappingData = createFieldMappingData(csvContent, dataIndex, true, customAttributes)
    const handleSamepleFieldChange = () => {
      return setCounter((index) => index + 1)
    }

    return (
      <CustomerImportConfirmationModal
        importDataValidationResult={importDataValidationResult}
        fieldMappingData={fieldMappingData}
        onSampleFieldChange={handleSamepleFieldChange}
        onCancel={closeModal}
        onNextStep={() => setStep(3)}
      />
    )
  }

  if (step === 3) {
    const handleImport = () => {
      const fieldMappingData = createFieldMappingData(csvContent, 1, true, customAttributes)
      const columnTypeMap = deriveColumnTypeMap(fieldMappingData)
      const commaSeparatedSkippedRows = validateImportData(csvContent, true, columnTypeMap)
        .errorRows.map((errorRow) => errorRow.rowNumber)
        .join(',')

      const normalizedFile =
        enableTypeDatetimeAttribute && Object.keys(columnTypeMap).length > 0
          ? buildNormalizedCsvFile(csvContent, columnTypeMap, csvFile.name)
          : csvFile
      onCsvUpload(
        normalizedFile,
        commaSeparatedTags,
        commaSeparatedSkippedRows,
        saveUserActivityLog
      )
      closeModal()
    }

    return (
      <CustomerImportTagsModal
        setCommaSeparatedTags={setCommaSeparatedTags}
        onCancel={closeModal}
        onImport={handleImport}
      />
    )
  }

  return null
}

function deriveColumnTypeMap(fieldMappingData) {
  if (!fieldMappingData) {
    return {}
  }
  return fieldMappingData.reduce((acc, entry, index) => {
    if (entry.field_type === 'datetime') {
      acc[index] = entry.field_type
    }
    return acc
  }, {})
}

/**
 * @param {any[][]} csvContent
 * @param {boolean} enableLanguage
 * @param {Record<number, 'datetime'>} [columnTypeMap]
 * @returns {import('organisms/CustomerImportConfirmationModal').ImportDataValidationResult}
 */
export function validateImportData(csvContent, enableLanguage, columnTypeMap) {
  const result = {
    importRecordsAmount: 0,
    errorRows: [],
  }

  for (let index = 1; index < csvContent.length; index++) {
    const errorRow = validateImportRow(
      csvContent[index],
      index,
      enableLanguage,
      columnTypeMap || {}
    )
    if (errorRow) {
      result.errorRows.push(errorRow)
    } else {
      result.importRecordsAmount += 1
    }
  }

  return result
}

/**
 * @param {any[]} csvDataRow
 * @param {number} rowNumber
 * @param {boolean} enableLanguage
 * @param {Record<number, 'datetime'>} columnTypeMap
 * @returns {import('organisms/CustomerImportConfirmationModal').ErrorRow | null}
 */
export function validateImportRow(csvDataRow, rowNumber, enableLanguage, columnTypeMap) {
  const emailIndex = 2
  const email = csvDataRow[emailIndex]
  const isEmailValid = constants.EMAIL_REGEX.test(email)

  const languageIndex = 4
  const language = csvDataRow[languageIndex]
  const isLanguageValid =
    !enableLanguage || !language || VALID_LANGUAGE_CODES.has(String(language).trim().toLowerCase())

  let invalidDatetime = null
  if (columnTypeMap) {
    for (const [colIndexStr, colType] of Object.entries(columnTypeMap)) {
      const columnIndex = Number(colIndexStr)
      const cellValue = csvDataRow[columnIndex]
      if (cellValue === undefined || cellValue === '') {
        continue
      }

      if (colType === 'datetime' && invalidDatetime === null && !isIsoDateTime(cellValue)) {
        invalidDatetime = cellValue
      }
    }
  }

  if (isEmailValid && isLanguageValid && invalidDatetime === null) {
    return null
  }

  const errorRow = { rowNumber }
  if (!isEmailValid) {
    errorRow.invalidEmail = email
  }
  if (!isLanguageValid) {
    errorRow.invalidLanguage = language
  }
  if (invalidDatetime !== null) {
    errorRow.invalidDatetime = invalidDatetime
  }
  return errorRow
}

/**
 * @param {any[][]} csvContent
 * @param {number} dataIndex
 * @param {boolean} enableLanguage
 * @param {any[] | null | undefined} customAttributes
 * @returns {any[]}
 */
export function createFieldMappingData(csvContent, dataIndex, enableLanguage, customAttributes) {
  const SIMPLESAT_CUSTOMER_IMPORT_CSV_HEADERS = enableLanguage
    ? ['First Name', 'Last Name', 'Email', 'Company', 'Language']
    : ['First Name', 'Last Name', 'Email', 'Company']
  const standardColumnCount = SIMPLESAT_CUSTOMER_IMPORT_CSV_HEADERS.length
  const header = csvContent[0]
  const data = csvContent[dataIndex]

  return header.map((headerInCsvFile, index) => {
    const isCustomColumn = index >= standardColumnCount
    const base = {
      columnHeader: headerInCsvFile,
      simplesatField: SIMPLESAT_CUSTOMER_IMPORT_CSV_HEADERS[index] || (
        <React.Fragment>
          {headerInCsvFile} <Label>custom</Label>
        </React.Fragment>
      ),
      sampleData: data[index],
    }

    if (isCustomColumn && customAttributes && customAttributes.length > 0) {
      const normalizedHeader = String(headerInCsvFile).trim().toLowerCase()
      const matchedAttr = customAttributes.find(
        (attr) => String(attr.field).trim().toLowerCase() === normalizedHeader
      )
      if (matchedAttr && Array.isArray(matchedAttr.field_type)) {
        const types = matchedAttr.field_type.map((t) => String(t).toLowerCase())
        if (types.length === 1 && types[0] === 'datetime') {
          base.field_type = 'datetime'
        }
      }
    }

    return base
  })
}

/**
 * Returns a new CSV File with every `'datetime'` column normalized to ISO 8601.
 * Unparseable cells are left as-is. Header row is never modified.
 *
 * @param {any[][]} csvContent
 * @param {Record<number, 'datetime'>} columnTypeMap
 * @param {string} originalFileName
 * @returns {File}
 */
export function buildNormalizedCsvFile(csvContent, columnTypeMap, originalFileName) {
  const normalizedRows = csvContent.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return row
    }
    if (!columnTypeMap || Object.keys(columnTypeMap).length === 0) {
      return row
    }
    const normalized = [...row]
    for (const [colIndexStr, colType] of Object.entries(columnTypeMap)) {
      if (colType !== 'datetime') {
        continue
      }
      const columnIndex = Number(colIndexStr)
      const cellValue = normalized[columnIndex]
      if (cellValue !== undefined && cellValue !== '') {
        const parsed = new Date(cellValue)
        if (!isNaN(parsed.getTime())) {
          normalized[columnIndex] = parsed.toISOString()
        }
      }
    }
    return normalized
  })

  const csvString = Papa.unparse(normalizedRows, { header: true })
  const blob = new Blob([csvString], { type: 'text/csv' })
  return new File([blob], originalFileName || 'normalized.csv', { type: 'text/csv' })
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/MultiStepCustomerImportModal/index.js